import { useState, useEffect } from 'react';
import { MenuItem as BackendMenuItem } from '../../lib/backendApi';
import { needsMenuSync, syncMenuFromBackend } from '../../lib/menuSync';
import { useMenuStore } from '../../stores/menuStore';
import ExcelUploader from './ExcelUploader';
import MenuConfirmationTable from './MenuConfirmationTable';
import PhotoUploader from './PhotoUploader';
import MenuItemsList from './MenuItemsList';

interface MenuOnboardingProps {
  tenantId: string;
}

type Step = 'check' | 'upload' | 'confirm' | 'photos';

export function MenuOnboarding({ tenantId }: MenuOnboardingProps) {
  const { loadMenuFromDatabase } = useMenuStore();
  const [currentStep, setCurrentStep] = useState<Step>('check');
  const [parsedItems, setParsedItems] = useState<BackendMenuItem[]>([]);
  const [_confirmedItems, setConfirmedItems] = useState<BackendMenuItem[]>([]);
  const [_overallConfidence, setOverallConfidence] = useState<number | undefined>();
  const [menuSynced, setMenuSynced] = useState<boolean | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Check if menu needs sync on mount
  useEffect(() => {
    checkMenuStatus();
  }, []);

  const checkMenuStatus = async () => {
    try {
      // needsMenuSync returns true if DB has 0 items
      const needsSync = await needsMenuSync();
      console.log('[MenuOnboarding] needsMenuSync:', needsSync);
      setMenuSynced(!needsSync);
    } catch (error) {
      console.error('[MenuOnboarding] Failed to check menu status:', error);
      // On error, assume menu exists (better UX than blocking)
      setMenuSynced(true);
    }
  };

  const handleSyncMenu = async () => {
    setSyncing(true);
    setSyncError(null);
    try {
      await syncMenuFromBackend(tenantId);
      // Refresh the menu store to load updated data from SQLite
      await loadMenuFromDatabase();
      setMenuSynced(true);
      alert('Menu synced successfully! You can now view it in the POS.');
    } catch (error) {
      console.error('[MenuOnboarding] Sync failed:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to sync menu';
      // Provide helpful message for 404 errors
      if (errorMsg.includes('404')) {
        setSyncError('Cloud menu sync is not available. Your local menu is already loaded from the database.');
        // Check if we have local menu items - if so, show as synced
        const hasLocalMenu = await needsMenuSync();
        if (!hasLocalMenu) {
          setMenuSynced(true);
        }
      } else {
        setSyncError(errorMsg);
        setMenuSynced(false);
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleExcelParsed = (items: BackendMenuItem[], confidence?: number) => {
    setParsedItems(items);
    setOverallConfidence(confidence);
    setCurrentStep('confirm');
  };

  const handleMenuConfirmed = (items: BackendMenuItem[]) => {
    setConfirmedItems(items);
    setCurrentStep('photos');
  };

  const handlePhotosComplete = () => {
    // Menu onboarding complete
    alert('Menu onboarding complete! All items have been uploaded.');
  };

  const handleBack = () => {
    if (currentStep === 'confirm') {
      setCurrentStep('check');
    } else if (currentStep === 'photos') {
      setCurrentStep('confirm');
    }
  };

  return (
    <div className="h-full flex flex-col">

      {/* Content Area */}
      <div className="flex-1 overflow-auto">
        {currentStep === 'check' && (
          <div className="h-full">
            {menuSynced === null ? (
              // Loading state
              <div className="flex items-center justify-center h-full">
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-2xl bg-accent/20 flex items-center justify-center mx-auto mb-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent"></div>
                  </div>
                  <p className="text-muted-foreground font-bold">Checking menu status...</p>
                </div>
              </div>
            ) : menuSynced ? (
              // Menu already synced - show menu items list (full width)
              <div className="animate-fade-in">
                <div className="mb-4 flex items-center justify-between glass-panel p-4 rounded-xl border border-border">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-lg font-bold">Menu Synced</h2>
                      <p className="text-xs text-muted-foreground">Your menu is ready to use in the POS</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSyncMenu}
                      disabled={syncing}
                      className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm font-bold uppercase tracking-wider hover:bg-white/10 disabled:opacity-50 transition-colors flex items-center gap-2"
                    >
                      {syncing ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-accent border-t-transparent"></div>
                          Syncing...
                        </>
                      ) : (
                        <>↻ Re-sync</>
                      )}
                    </button>
                    <button
                      onClick={() => setCurrentStep('upload')}
                      className="px-3 py-2 rounded-xl bg-accent text-white text-sm font-bold uppercase tracking-wider shadow-lg shadow-accent/20 hover:scale-105 transition-all"
                    >
                      Upload Custom
                    </button>
                  </div>
                </div>
                <MenuItemsList onRefresh={checkMenuStatus} />
              </div>
            ) : (
              // Menu not synced - centered card
              <div className="flex items-center justify-center h-full p-8">
                <div className="max-w-lg w-full glass-panel rounded-2xl border border-border p-8 animate-fade-in text-center">
                  <div className="w-16 h-16 bg-accent/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <span className="text-3xl">📋</span>
                  </div>
                  <h2 className="text-xl font-black uppercase mb-2">No Menu Found</h2>
                  <p className="text-muted-foreground mb-6 text-sm">
                    Sync your menu from the HandsFree platform or upload a new menu document to get started.
                  </p>
                  {syncError && (
                    <div className="mb-6 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                      <p className="text-red-400 text-sm">{syncError}</p>
                    </div>
                  )}
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={handleSyncMenu}
                      disabled={syncing}
                      className="px-5 py-2.5 rounded-xl bg-accent text-white font-bold uppercase tracking-widest text-xs shadow-lg shadow-accent/20 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 transition-all flex items-center gap-2"
                    >
                      {syncing ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Syncing...
                        </>
                      ) : (
                        <>Sync from HandsFree</>
                      )}
                    </button>
                    <button
                      onClick={() => setCurrentStep('upload')}
                      className="px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-foreground font-bold uppercase tracking-widest text-xs hover:bg-white/10 transition-colors"
                    >
                      Upload Document
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {currentStep === 'upload' && (
          <ExcelUploader
            tenantId={tenantId}
            onParsed={handleExcelParsed}
          />
        )}

        {currentStep === 'confirm' && (
          <MenuConfirmationTable
            tenantId={tenantId}
            items={parsedItems}
            onConfirmed={handleMenuConfirmed}
            onBack={handleBack}
          />
        )}

        {currentStep === 'photos' && (
          <PhotoUploader
            tenantId={tenantId}
            onComplete={handlePhotosComplete}
            onBack={handleBack}
          />
        )}
      </div>
    </div>
  );
}

export default MenuOnboarding;
